import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Option,
  Spinner,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  validateManualTimeEntry,
  type ManualTimeEntryInput,
  type ManualTimeEntryIssue,
  type TimeEntryOptions,
} from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { MOBILE, SHRINKABLE } from '../responsive';

import { useTimeAdminApi } from './api';
import { toErrorMessage } from './errors';
import { fromLocalInputValue, toLocalInputValue } from './format';

const useStyles = makeStyles({
  /**
   * 🔥 This carried `minWidth: 380px`, which is what cut the right-hand side off
   * (Michael, 2026-09-10). A flex item that refuses to shrink below 380px forces
   * the dialog wider than the viewport on a phone, and the overflow lands
   * outside the surface where it cannot be reached or scrolled to.
   *
   * `min(380px, 100%)` keeps the comfortable width on a desktop and gives it up
   * entirely when there is less room, which is the behaviour that was intended.
   */
  form: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    width: 'min(380px, 100%)',
    ...SHRINKABLE,
  },
  row: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  grow: { flexGrow: 1, minWidth: '170px' },
  input: {
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase300,
    padding: '5px 8px',
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    width: '100%',
  },
  // Every broken rule at once, wrapped — never truncated.
  issues: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    color: tokens.colorPaletteRedForeground1,
    whiteSpace: 'normal',
  },
  hint: { color: tokens.colorNeutralForeground3 },
  /**
   * Fluent's dialog surface has its own fixed max-width plus horizontal padding.
   * On a phone that leaves the content boxed into a column narrower than the
   * screen; below the breakpoint the dialog takes the viewport instead.
   */
  surface: {
    [MOBILE]: {
      width: '100vw',
      maxWidth: '100vw',
      // NOT ...SHRINKABLE here: its maxWidth:'100%' would override the 100vw above.
      minWidth: 0,
    },
  },
});

const EMPTY: ManualTimeEntryInput = {
  employeeId: '',
  departmentId: '',
  clockInAt: '',
  clockOutAt: '',
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful save so the grid can reload. */
  onCreated: () => void;
}

/**
 * "New Time Entry" — a supervisor records a completed shift for an employee who
 * forgot to clock in.
 *
 * The employee list comes from `GET /time/session-options`, which returns only
 * the people this caller may add time for. That is a convenience, not the
 * boundary: the API re-checks on write, so a hidden name is not a security
 * measure.
 *
 * The entry is created UNAPPROVED and joins the normal review queue, so hours
 * still pass an explicit approval before they can reach payroll.
 */
export function NewTimeEntryDialog({ open, onOpenChange, onCreated }: Props): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();

  const [options, setOptions] = useState<TimeEntryOptions | null>(null);
  const [form, setForm] = useState<ManualTimeEntryInput>({ ...EMPTY });
  const [issues, setIssues] = useState<readonly ManualTimeEntryIssue[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Load the pickers when the dialog opens, and reset anything left from a
  // previous use so a stale employee can never be resubmitted.
  useEffect(() => {
    if (!open) {
      return;
    }
    setForm({ ...EMPTY });
    setIssues([]);
    setSaveError(null);
    let cancelled = false;
    void (async () => {
      try {
        const opts = await api.getSessionOptions();
        if (!cancelled) {
          setOptions(opts);
        }
      } catch (err) {
        if (!cancelled) {
          setSaveError(toErrorMessage(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, open]);

  const patch = useCallback((next: Partial<ManualTimeEntryInput>) => {
    setForm((prev) => ({ ...prev, ...next }));
  }, []);

  const employeeName = useMemo(
    () => options?.employees.find((e) => e.id === form.employeeId)?.name ?? '',
    [options, form.employeeId]
  );
  const departmentName = useMemo(
    () => options?.departments.find((d) => d.id === form.departmentId)?.name ?? '',
    [options, form.departmentId]
  );

  const submit = useCallback(async (): Promise<void> => {
    if (options === null) {
      return;
    }
    // Validate HERE first so every problem is listed without a round trip; the
    // API runs the identical rules and remains the authority.
    const found = validateManualTimeEntry(form, {
      employeeIds: new Set(options.employees.map((e) => e.id)),
      departmentIds: new Set(options.departments.map((d) => d.id)),
      // Overlap is checked server-side against the real sessions; the client
      // cannot see another supervisor's rows, so it does not guess here.
      existing: [],
      now: new Date().toISOString(),
    });
    setIssues(found);
    if (found.length > 0) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      /**
       * OMIT the clock-out when it is blank rather than sending an empty string: an
       * open entry has no end, and `''` is not an ISO-8601 instant. The API tolerates
       * both now, but sending a field that means "absent" as a value that means
       * "unparseable" is exactly how this broke the first time.
       *
       * Built by hand rather than spread — `exactOptionalPropertyTypes` refuses an
       * explicit `undefined` for an optional property, which is the same distinction
       * the bug was about.
       */
      const open = form.clockOutAt === undefined || form.clockOutAt === '';
      await api.createSession(
        open
          ? {
              employeeId: form.employeeId,
              departmentId: form.departmentId,
              clockInAt: form.clockInAt,
            }
          : form
      );
      onCreated();
      onOpenChange(false);
    } catch (err) {
      setSaveError(toErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [api, form, onCreated, onOpenChange, options]);

  return (
    <Dialog open={open} onOpenChange={(_, d) => onOpenChange(d.open)}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>New time entry</DialogTitle>
          <DialogContent>
            {options === null ? (
              <Spinner size="tiny" label="Loading…" />
            ) : (
              <div className={styles.form}>
                <Field label="Employee">
                  <Dropdown
                    aria-label="Employee"
                    placeholder="Select employee"
                    value={employeeName}
                    selectedOptions={form.employeeId === '' ? [] : [form.employeeId]}
                    onOptionSelect={(_, d) => {
                      const picked = options.employees.find((e) => e.id === d.optionValue);
                      // Pre-fill the department from the employee's assignment;
                      // still editable, since a shift may be in another one.
                      patch({
                        employeeId: d.optionValue ?? '',
                        ...(picked?.departmentId != null && form.departmentId === ''
                          ? { departmentId: picked.departmentId }
                          : {}),
                      });
                    }}
                  >
                    {options.employees.map((e) => (
                      <Option key={e.id} value={e.id} text={e.name}>
                        {e.name}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>

                <Field label="Department">
                  <Dropdown
                    aria-label="Department"
                    placeholder="Select department"
                    value={departmentName}
                    selectedOptions={form.departmentId === '' ? [] : [form.departmentId]}
                    onOptionSelect={(_, d) => patch({ departmentId: d.optionValue ?? '' })}
                  >
                    {options.departments.map((dep) => (
                      <Option key={dep.id} value={dep.id} text={dep.name}>
                        {dep.name}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>

                <div className={styles.row}>
                  <Field label="Clock in" className={styles.grow}>
                    <input
                      type="datetime-local"
                      className={styles.input}
                      aria-label="Clock in"
                      value={toLocalInputValue(form.clockInAt)}
                      onChange={(e) =>
                        patch({ clockInAt: fromLocalInputValue(e.target.value) ?? '' })
                      }
                    />
                  </Field>
                  <Field label="Clock out (optional)" className={styles.grow}>
                    <input
                      type="datetime-local"
                      className={styles.input}
                      aria-label="Clock out"
                      value={toLocalInputValue(form.clockOutAt)}
                      onChange={(e) =>
                        patch({ clockOutAt: fromLocalInputValue(e.target.value) ?? '' })
                      }
                    />
                  </Field>
                </div>

                <Text className={styles.hint}>
                  Leave <strong>Clock out</strong> blank to clock someone IN and let them clock
                  out at the kiosk when they leave. Fill both ends to record a shift that is
                  already finished. Either way the entry is added unapproved, so it still needs
                  approving like any other time.
                </Text>

                {issues.length > 0 ? (
                  <div className={styles.issues} role="alert">
                    {issues.map((issue) => (
                      <Text key={`${issue.field}:${issue.message}`}>{issue.message}</Text>
                    ))}
                  </div>
                ) : null}
                {saveError !== null ? (
                  <div className={styles.issues} role="alert">
                    <Text>{saveError}</Text>
                  </div>
                ) : null}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={() => void submit()}
              disabled={saving || options === null}
            >
              {saving ? <Spinner size="tiny" /> : 'Add entry'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
